/*  WhyInvisiqueTab — Admin CRUD for the Why Invisique CMS.
    iter100.206

    Manages two sections rendered on the public /why-invisique page:
      1. Leadership team cards ("Built by leaders") — name / role / bio / photo
      2. Certifications strip ("Trusted & certified by") — name / sub

    Backend endpoints used:
      Leaders:
        GET    /api/admin/leaders
        POST   /api/admin/leaders
        PATCH  /api/admin/leaders/{id}
        DELETE /api/admin/leaders/{id}
        POST   /api/admin/leaders/{id}/photo    (multipart)
      Certifications:
        GET    /api/admin/certifications
        POST   /api/admin/certifications
        PATCH  /api/admin/certifications/{id}
        DELETE /api/admin/certifications/{id}
*/
import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
    AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
    Plus, PencilSimple, Trash, Eye, EyeSlash, Upload,
    UsersThree, Certificate, ArrowsDownUp, ImageSquare,
} from "@phosphor-icons/react";


/* ─────────────────────────── Reusable bits ─────────────────────────── */

function SectionHeader({ icon: Icon, title, subtitle, onNew, newLabel, testid }) {
    return (
        <div className="flex items-start justify-between gap-4 mb-4" data-testid={testid}>
            <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-xl bg-primary/10 grid place-items-center shrink-0">
                    <Icon size={20} weight="duotone" className="text-primary" />
                </div>
                <div>
                    <h3 className="font-display text-lg font-bold tracking-tight">{title}</h3>
                    <p className="text-xs text-muted-foreground mt-0.5 max-w-md">{subtitle}</p>
                </div>
            </div>
            <Button size="sm" onClick={onNew} className="rounded-full shrink-0" data-testid={`${testid}-add`}>
                <Plus size={12} weight="bold" className="mr-1" /> {newLabel}
            </Button>
        </div>
    );
}


/* ─────────────────────────── Leaders ─────────────────────────── */

const EMPTY_LEADER = {
    id: null, name: "", role: "", bio: "", image_url: "",
    display_order: 100, is_published: true,
};

function LeaderForm({ open, initial, onClose, onSaved }) {
    const [form, setForm] = useState(initial || EMPTY_LEADER);
    const [busy, setBusy] = useState(false);
    const [uploading, setUploading] = useState(false);
    useEffect(() => { setForm(initial || EMPTY_LEADER); }, [initial, open]);

    const save = async () => {
        if (!form.name.trim() || !form.role.trim()) {
            toast.error("Name and role are required");
            return;
        }
        setBusy(true);
        try {
            const payload = {
                name: form.name,
                role: form.role,
                bio: form.bio || "",
                image_url: form.image_url || "",
                display_order: Number(form.display_order) || 100,
                is_published: !!form.is_published,
            };
            if (form.id) {
                await api.patch(`/admin/leaders/${form.id}`, payload);
                toast.success("Leader updated");
            } else {
                await api.post("/admin/leaders", payload);
                toast.success("Leader added");
            }
            onSaved?.();
            onClose();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Save failed");
        } finally {
            setBusy(false);
        }
    };

    const uploadPhoto = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (!form.id) {
            toast.error("Save the leader first before uploading a photo");
            e.target.value = "";
            return;
        }
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            fd.append("alt", form.name);
            const r = await api.post(`/admin/leaders/${form.id}/photo`, fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setForm({ ...form, image_url: r.data.image_url });
            toast.success("Photo uploaded");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Upload failed");
        } finally {
            setUploading(false);
            e.target.value = "";
        }
    };

    return (
        <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
            <DialogContent className="max-w-lg" data-testid="leader-form-dialog">
                <DialogHeader>
                    <DialogTitle>{form.id ? "Edit leader" : "Add leader"}</DialogTitle>
                    <DialogDescription>
                        This card renders on the public <b>/why-invisique</b> page under &quot;Built by leaders&quot;.
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-3 py-1">
                    <div>
                        <Label className="text-xs font-semibold">Name</Label>
                        <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                            placeholder="Dr. Ritu Malhotra" className="h-9 text-sm" data-testid="leader-form-name" />
                    </div>
                    <div>
                        <Label className="text-xs font-semibold">Role</Label>
                        <Input value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}
                            placeholder="Chief Clinical Officer" className="h-9 text-sm" data-testid="leader-form-role" />
                    </div>
                    <div>
                        <Label className="text-xs font-semibold">Bio (short credentials)</Label>
                        <Textarea rows={2} value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })}
                            placeholder="25+ years orthodontic practice · Former President, IOS"
                            className="text-sm" data-testid="leader-form-bio" />
                    </div>
                    <div>
                        <Label className="text-xs font-semibold">Photo</Label>
                        <div className="flex items-center gap-3 mt-1">
                            {form.image_url ? (
                                <img src={form.image_url} alt="preview"
                                    className="w-16 h-20 rounded-lg object-cover border border-border" />
                            ) : (
                                <div className="w-16 h-20 rounded-lg bg-secondary grid place-items-center border border-border">
                                    <ImageSquare size={22} className="text-muted-foreground" />
                                </div>
                            )}
                            <div className="flex-1 space-y-1.5">
                                <Input value={form.image_url}
                                    onChange={(e) => setForm({ ...form, image_url: e.target.value })}
                                    placeholder="/marketing/leader.png or paste URL"
                                    className="h-8 text-xs" data-testid="leader-form-image-url" />
                                <label className="inline-flex items-center gap-1.5 text-xs font-semibold text-primary cursor-pointer hover:underline">
                                    <Upload size={12} weight="bold" />
                                    {uploading ? "Uploading…" : (form.id ? "Upload photo" : "Save first, then upload")}
                                    <input type="file" accept="image/*" onChange={uploadPhoto} className="hidden"
                                        disabled={!form.id || uploading} data-testid="leader-form-photo-input" />
                                </label>
                            </div>
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label className="text-xs font-semibold">Display order</Label>
                            <Input type="number" value={form.display_order}
                                onChange={(e) => setForm({ ...form, display_order: e.target.value })}
                                className="h-9 text-sm" data-testid="leader-form-order" />
                        </div>
                        <div>
                            <Label className="text-xs font-semibold">Published</Label>
                            <label className="mt-2 flex items-center gap-2 text-sm cursor-pointer">
                                <input type="checkbox" checked={form.is_published}
                                    onChange={(e) => setForm({ ...form, is_published: e.target.checked })}
                                    data-testid="leader-form-published" />
                                <span>{form.is_published ? "Live on site" : "Draft (hidden)"}</span>
                            </label>
                        </div>
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="ghost" onClick={onClose} disabled={busy}>Cancel</Button>
                    <Button onClick={save} disabled={busy} className="rounded-full" data-testid="leader-form-submit">
                        {busy ? "Saving…" : (form.id ? "Save changes" : "Add leader")}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function LeadersSection() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null);
    const [showForm, setShowForm] = useState(false);

    const load = () => {
        setLoading(true);
        api.get("/admin/leaders")
            .then((r) => setItems(r.data?.items || []))
            .catch(() => setItems([]))
            .finally(() => setLoading(false));
    };
    useEffect(() => { load(); }, []);

    const openNew = () => { setEditing(null); setShowForm(true); };
    const openEdit = (row) => { setEditing(row); setShowForm(true); };
    const togglePub = async (row) => {
        try {
            await api.patch(`/admin/leaders/${row.id}`, {
                name: row.name, role: row.role, bio: row.bio || "", image_url: row.image_url || "",
                display_order: row.display_order, is_published: !row.is_published,
            });
            toast.success(row.is_published ? "Hidden" : "Published");
            load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Toggle failed");
        }
    };
    const remove = async (row) => {
        try {
            await api.delete(`/admin/leaders/${row.id}`);
            toast.success("Leader removed");
            load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Delete failed");
        }
    };

    return (
        <div>
            <SectionHeader
                icon={UsersThree}
                title="Built by leaders"
                subtitle="Portrait cards on the /why-invisique page. Flexible 1–6 people. Middle card is staggered when count = 3."
                onNew={openNew}
                newLabel="Add leader"
                testid="leaders-header"
            />
            {loading ? (
                <div className="text-xs text-muted-foreground py-10 text-center">Loading…</div>
            ) : items.length === 0 ? (
                <div className="text-sm text-muted-foreground py-10 text-center border border-dashed border-border rounded-xl" data-testid="leaders-empty">
                    No leaders yet. Click <b>Add leader</b> to build the section.
                </div>
            ) : (
                <div className="space-y-2" data-testid="leaders-list">
                    {items.map((row) => (
                        <div key={row.id} className="flex items-center gap-3 p-3 border border-border rounded-xl bg-card" data-testid={`leader-row-${row.id}`}>
                            {row.image_url ? (
                                <img src={row.image_url} alt={row.name}
                                    className="w-12 h-14 rounded-lg object-cover border border-border shrink-0" />
                            ) : (
                                <div className="w-12 h-14 rounded-lg bg-secondary grid place-items-center border border-border shrink-0">
                                    <ImageSquare size={18} className="text-muted-foreground" />
                                </div>
                            )}
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <span className="font-semibold text-sm">{row.name}</span>
                                    <span className="text-[10px] uppercase tracking-wider font-bold text-primary/70">{row.role}</span>
                                    {!row.is_published && (
                                        <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 border border-slate-200">Draft</span>
                                    )}
                                    <span className="text-[10px] text-muted-foreground inline-flex items-center gap-0.5">
                                        <ArrowsDownUp size={10} /> {row.display_order}
                                    </span>
                                </div>
                                <p className="text-[11px] text-muted-foreground mt-0.5 truncate">{row.bio}</p>
                            </div>
                            <div className="flex items-center gap-1 shrink-0">
                                <Button size="sm" variant="ghost" onClick={() => togglePub(row)} className="h-8 px-2"
                                    title={row.is_published ? "Unpublish" : "Publish"}
                                    data-testid={`leader-toggle-${row.id}`}>
                                    {row.is_published ? <Eye size={14} /> : <EyeSlash size={14} />}
                                </Button>
                                <Button size="sm" variant="ghost" onClick={() => openEdit(row)} className="h-8 px-2"
                                    data-testid={`leader-edit-${row.id}`}>
                                    <PencilSimple size={14} />
                                </Button>
                                <AlertDialog>
                                    <AlertDialogTrigger asChild>
                                        <Button size="sm" variant="ghost" className="h-8 px-2 text-rose-600 hover:bg-rose-50" data-testid={`leader-delete-${row.id}`}>
                                            <Trash size={14} />
                                        </Button>
                                    </AlertDialogTrigger>
                                    <AlertDialogContent>
                                        <AlertDialogHeader>
                                            <AlertDialogTitle>Remove {row.name}?</AlertDialogTitle>
                                            <AlertDialogDescription>
                                                This will remove the card from the public Why Invisique page immediately.
                                            </AlertDialogDescription>
                                        </AlertDialogHeader>
                                        <AlertDialogFooter>
                                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                                            <AlertDialogAction onClick={() => remove(row)} className="bg-rose-600 hover:bg-rose-700" data-testid={`leader-delete-confirm-${row.id}`}>
                                                Remove
                                            </AlertDialogAction>
                                        </AlertDialogFooter>
                                    </AlertDialogContent>
                                </AlertDialog>
                            </div>
                        </div>
                    ))}
                </div>
            )}
            <LeaderForm open={showForm} initial={editing} onClose={() => setShowForm(false)} onSaved={load} />
        </div>
    );
}


/* ─────────────────────────── Certifications ─────────────────────────── */

const EMPTY_CERT = { id: null, name: "", sub: "", display_order: 100, is_published: true };

function CertForm({ open, initial, onClose, onSaved }) {
    const [form, setForm] = useState(initial || EMPTY_CERT);
    const [busy, setBusy] = useState(false);
    useEffect(() => { setForm(initial || EMPTY_CERT); }, [initial, open]);

    const save = async () => {
        if (!form.name.trim()) {
            toast.error("Name is required");
            return;
        }
        setBusy(true);
        try {
            const payload = {
                name: form.name,
                sub: form.sub || "",
                display_order: Number(form.display_order) || 100,
                is_published: !!form.is_published,
            };
            if (form.id) {
                await api.patch(`/admin/certifications/${form.id}`, payload);
                toast.success("Certification updated");
            } else {
                await api.post("/admin/certifications", payload);
                toast.success("Certification added");
            }
            onSaved?.();
            onClose();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Save failed");
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
            <DialogContent className="max-w-md" data-testid="cert-form-dialog">
                <DialogHeader>
                    <DialogTitle>{form.id ? "Edit certification" : "Add certification"}</DialogTitle>
                    <DialogDescription>
                        Chip in the &quot;Trusted &amp; certified by&quot; strip on <b>/why-invisique</b>.
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-3 py-1">
                    <div>
                        <Label className="text-xs font-semibold">Name</Label>
                        <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                            placeholder="ISO 13485" className="h-9 text-sm" data-testid="cert-form-name" />
                    </div>
                    <div>
                        <Label className="text-xs font-semibold">Sub-label</Label>
                        <Input value={form.sub} onChange={(e) => setForm({ ...form, sub: e.target.value })}
                            placeholder="Medical devices QMS" className="h-9 text-sm" data-testid="cert-form-sub" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label className="text-xs font-semibold">Display order</Label>
                            <Input type="number" value={form.display_order}
                                onChange={(e) => setForm({ ...form, display_order: e.target.value })}
                                className="h-9 text-sm" data-testid="cert-form-order" />
                        </div>
                        <div>
                            <Label className="text-xs font-semibold">Published</Label>
                            <label className="mt-2 flex items-center gap-2 text-sm cursor-pointer">
                                <input type="checkbox" checked={form.is_published}
                                    onChange={(e) => setForm({ ...form, is_published: e.target.checked })}
                                    data-testid="cert-form-published" />
                                <span>{form.is_published ? "Live" : "Draft"}</span>
                            </label>
                        </div>
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="ghost" onClick={onClose} disabled={busy}>Cancel</Button>
                    <Button onClick={save} disabled={busy} className="rounded-full" data-testid="cert-form-submit">
                        {busy ? "Saving…" : (form.id ? "Save changes" : "Add certification")}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function CertificationsSection() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null);
    const [showForm, setShowForm] = useState(false);

    const load = () => {
        setLoading(true);
        api.get("/admin/certifications")
            .then((r) => setItems(r.data?.items || []))
            .catch(() => setItems([]))
            .finally(() => setLoading(false));
    };
    useEffect(() => { load(); }, []);

    const openNew = () => { setEditing(null); setShowForm(true); };
    const openEdit = (row) => { setEditing(row); setShowForm(true); };
    const togglePub = async (row) => {
        try {
            await api.patch(`/admin/certifications/${row.id}`, {
                name: row.name, sub: row.sub || "",
                display_order: row.display_order, is_published: !row.is_published,
            });
            toast.success(row.is_published ? "Hidden" : "Published");
            load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Toggle failed");
        }
    };
    const remove = async (row) => {
        try {
            await api.delete(`/admin/certifications/${row.id}`);
            toast.success("Certification removed");
            load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Delete failed");
        }
    };

    return (
        <div className="mt-10 pt-8 border-t border-border">
            <SectionHeader
                icon={Certificate}
                title="Trusted & certified by"
                subtitle="Certification chips (ISO 13485, CDSCO, etc.) that render below the Team section."
                onNew={openNew}
                newLabel="Add certification"
                testid="certs-header"
            />
            {loading ? (
                <div className="text-xs text-muted-foreground py-10 text-center">Loading…</div>
            ) : items.length === 0 ? (
                <div className="text-sm text-muted-foreground py-10 text-center border border-dashed border-border rounded-xl" data-testid="certs-empty">
                    No certifications yet. Click <b>Add certification</b>.
                </div>
            ) : (
                <div className="space-y-2" data-testid="certs-list">
                    {items.map((row) => (
                        <div key={row.id} className="flex items-center gap-3 p-3 border border-border rounded-xl bg-card" data-testid={`cert-row-${row.id}`}>
                            <div className="w-10 h-10 rounded-xl bg-primary/10 grid place-items-center shrink-0">
                                <Certificate size={18} weight="duotone" className="text-primary" />
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <span className="font-semibold text-sm">{row.name}</span>
                                    {!row.is_published && (
                                        <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 border border-slate-200">Draft</span>
                                    )}
                                    <span className="text-[10px] text-muted-foreground inline-flex items-center gap-0.5">
                                        <ArrowsDownUp size={10} /> {row.display_order}
                                    </span>
                                </div>
                                <p className="text-[11px] text-muted-foreground mt-0.5 truncate">{row.sub}</p>
                            </div>
                            <div className="flex items-center gap-1 shrink-0">
                                <Button size="sm" variant="ghost" onClick={() => togglePub(row)} className="h-8 px-2"
                                    title={row.is_published ? "Unpublish" : "Publish"}
                                    data-testid={`cert-toggle-${row.id}`}>
                                    {row.is_published ? <Eye size={14} /> : <EyeSlash size={14} />}
                                </Button>
                                <Button size="sm" variant="ghost" onClick={() => openEdit(row)} className="h-8 px-2"
                                    data-testid={`cert-edit-${row.id}`}>
                                    <PencilSimple size={14} />
                                </Button>
                                <AlertDialog>
                                    <AlertDialogTrigger asChild>
                                        <Button size="sm" variant="ghost" className="h-8 px-2 text-rose-600 hover:bg-rose-50" data-testid={`cert-delete-${row.id}`}>
                                            <Trash size={14} />
                                        </Button>
                                    </AlertDialogTrigger>
                                    <AlertDialogContent>
                                        <AlertDialogHeader>
                                            <AlertDialogTitle>Remove {row.name}?</AlertDialogTitle>
                                            <AlertDialogDescription>
                                                This chip will disappear from the public Why Invisique page immediately.
                                            </AlertDialogDescription>
                                        </AlertDialogHeader>
                                        <AlertDialogFooter>
                                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                                            <AlertDialogAction onClick={() => remove(row)} className="bg-rose-600 hover:bg-rose-700" data-testid={`cert-delete-confirm-${row.id}`}>
                                                Remove
                                            </AlertDialogAction>
                                        </AlertDialogFooter>
                                    </AlertDialogContent>
                                </AlertDialog>
                            </div>
                        </div>
                    ))}
                </div>
            )}
            <CertForm open={showForm} initial={editing} onClose={() => setShowForm(false)} onSaved={load} />
        </div>
    );
}


/* ─────────────────────────── Root ─────────────────────────── */

export default function WhyInvisiqueTab() {
    return (
        <div className="max-w-3xl" data-testid="why-invisique-tab">
            <LeadersSection />
            <CertificationsSection />
        </div>
    );
}
